import { useState, useMemo, lazy, Suspense } from 'react';
import { Check, Lock } from 'lucide-react';
import { SkipToContent } from '@/components/landing/SkipToContent';
import { FormAlt } from '@/components/landing/FormAlt';
import { FlagVideoBackground } from '@/components/landing/FlagVideoBackground';
import { SocialProofBar } from '@/components/landing/SocialProof';
import axios from 'axios';

const TrustBadges = lazy(() => import('@/components/landing/TrustBadges').then(m => ({ default: m.TrustBadges })));
const DisclaimerSection = lazy(() => import('@/components/landing/DisclaimerSection').then(m => ({ default: m.DisclaimerSection })));
const FooterAlt = lazy(() => import('@/components/landing/FooterAlt').then(m => ({ default: m.FooterAlt })));
const SuccessModalAlt = lazy(() => import('@/components/landing/SuccessModalAlt').then(m => ({ default: m.SuccessModalAlt })));
const StickyCta = lazy(() => import('@/components/landing/StickyCta').then(m => ({ default: m.StickyCta })));
const CookieBanner = lazy(() => import('@/components/landing/CookieBanner').then(m => ({ default: m.CookieBanner })));
const SocialProofStrip = lazy(() => import('@/components/landing/SocialProof').then(m => ({ default: m.SocialProofStrip })));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function LandingPage() {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [submittedData, setSubmittedData] = useState({ email: '', firstName: '' });

  // Capture URL tracking params once on mount
  const trackingParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      c1: params.get('c1') || '',
      c2: params.get('c2') || '',
      c3: params.get('c3') || '',
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || '',
    };
  }, []);

  const handleFormSubmit = async (data) => {
    try {
      await axios.post(`${API}/leads`, { ...data, ...trackingParams });
    } catch (e) {
      console.error('Failed to submit lead:', e);
    }
    setSubmittedData({ email: data.email, firstName: data.firstName });
    setIsSuccessModalOpen(true);
  };

  return (
    <div className="bg-warm-50" data-testid="landing-page">
      <SkipToContent />

      <main id="main-content">
        {/* HERO */}
        <section className="relative pt-4 xs:pt-5 sm:pt-10 pb-6 sm:pb-12 overflow-hidden" aria-label="Hero" data-testid="hero-section">
          <FlagVideoBackground />

          <div className="relative z-10 container mx-auto px-3 sm:px-4 max-w-2xl">
            {/* Badge */}
            <div className="flex justify-center mb-3 sm:mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-warm-950/90 text-white text-[11px] font-semibold rounded-full" data-testid="hero-badge">
                Canadian Residents Only
              </span>
            </div>

            {/* Headline */}
            <div className="text-center mb-3 sm:mb-5">
              <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-warm-950 mb-1 sm:mb-2" data-testid="hero-headline">
                Canada Care
              </h1>
              <p className="text-sm xs:text-base sm:text-lg text-warm-600 font-medium">Connecting Canadians aged 50-80 to Life Insurance</p>
            </div>

            {/* Trust pills */}
            <div className="flex flex-col xs:flex-row items-center justify-center gap-1 xs:gap-2 sm:gap-4 mb-3 sm:mb-4">
              {[
                'No medical exams\u00B2',
                'Coverage up to $25,000\u00B9',
                'Free assessment, no obligation',
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] xs:text-xs text-warm-700" data-testid="trust-pill">
                  <Check className="w-3.5 h-3.5 text-warm-600 flex-shrink-0" /> {t}
                </span>
              ))}
            </div>

            {/* Social proof bar */}
            <SocialProofBar />

            {/* Form */}
            <div id="quote-form" data-testid="quote-form-section">
              <div className="text-center mb-3 sm:mb-5">
                <h2 className="font-bold text-warm-950 text-sm xs:text-base sm:text-lg">Check Your Eligibility in Under 60 Seconds</h2>
                <p className="text-[11px] xs:text-xs sm:text-sm text-warm-500 mt-0.5 sm:mt-1">Answer a few simple questions to see your coverage options</p>
              </div>

              <FormAlt onSubmit={handleFormSubmit} />

              <div className="flex items-center justify-center gap-1.5 mt-2 sm:mt-3">
                <Lock className="w-3 h-3 text-warm-400" />
                <p className="text-[9px] xs:text-[10px] text-warm-400">Your information is secure and shared only with licensed advisors</p>
              </div>
            </div>

            {/* Underwriter strip */}
            <div className="mt-4 sm:mt-6 text-center">
              <p className="text-[9px] xs:text-[10px] text-warm-400">
                Powered by <strong className="text-warm-600">Canada Care</strong> &middot; Connecting you to licensed insurance advisors
              </p>
            </div>
          </div>
        </section>

        {/* SOCIAL PROOF STRIP */}
        <Suspense fallback={null}>
          <SocialProofStrip />
        </Suspense>

        {/* TRUST BADGES */}
        <Suspense fallback={null}>
          <TrustBadges />
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <DisclaimerSection />
      </Suspense>

      <Suspense fallback={null}>
        <FooterAlt />
      </Suspense>

      <Suspense fallback={null}>
        <StickyCta />
        <CookieBanner />
      </Suspense>

      <Suspense fallback={null}>
        <SuccessModalAlt
          isOpen={isSuccessModalOpen}
          onClose={() => setIsSuccessModalOpen(false)}
          email={submittedData.email}
          firstName={submittedData.firstName}
        />
      </Suspense>
    </div>
  );
}
